/** Reolink camera battery helpers — not sensor batteryVoltage. */

const asArray = (value) => (Array.isArray(value) ? value : []);

export const extractReolinkCameras = (res) => {
  if (!res || typeof res !== 'object') return [];
  if (Array.isArray(res.cameras)) return res.cameras;
  if (Array.isArray(res.data?.cameras)) return res.data.cameras;
  if (Array.isArray(res.reolinkBatteries)) return res.reolinkBatteries;
  if (Array.isArray(res.data?.reolinkBatteries)) return res.data.reolinkBatteries;
  const snap = res.reolinkBattery || res.data?.reolinkBattery;
  if (snap && typeof snap === 'object' && !Array.isArray(snap)) return [snap];
  return [];
};

/** FTP name like "Indusinformatics Escambia 2_00_..." or glued "Destin1_00_..." → 1/2 */
export const getCameraNumberFromFileName = (fileName) => {
  const match = String(fileName || '').match(/(\d+)_\d{2}_\d{8,}/);
  if (!match) return null;
  const n = Number(match[1]);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Camera overlay clock from FTP name: ..._20260819012852.mp4 → 19 Aug 2026 / 01:28:52 AM */
export const formatFtpFileClock = (fileName) => {
  const match = String(fileName || '').match(/(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})(?:\.[a-z0-9]+)?$/i);
  if (!match) return null;
  const hour = Number(match[4]);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return {
    date: `${Number(match[3])} ${MONTHS_SHORT[Number(match[2]) - 1]} ${match[1]}`,
    time: `${String(hour12).padStart(2, '0')}:${match[5]}:${match[6]} ${ampm}`,
  };
};

/**
 * List + player Date/Time: camera recording clock, not S3 lastModified and not a
 * second timezone conversion of recordedAt (that turned 01:28 AM into 03:58 PM).
 */
export const formatReolinkRecordingClock = (video) => {
  const fromName = formatFtpFileClock(video?.fileName || video?.s3Key);
  if (fromName) return fromName;
  const recordedAt = video?.recordedAt;
  if (recordedAt == null || recordedAt === '') return { date: '—', time: '—' };
  const d = new Date(recordedAt);
  if (Number.isNaN(d.getTime())) return { date: '—', time: '—' };
  const dateOpts = { timeZone: 'UTC', day: '2-digit', month: 'short', year: 'numeric' };
  const timeOpts = {
    timeZone: 'UTC',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  };
  return {
    date: d.toLocaleDateString('en-GB', dateOpts),
    time: d.toLocaleTimeString('en-US', timeOpts),
  };
};

/** Timestamp baked into FTP names: ..._20260818013553.mp4 */
export const getReolinkFileTimestampMs = (fileName) => {
  const match = String(fileName || '').match(/(\d{8})(\d{6})(?:\.[a-z0-9]+)?$/i);
  if (!match) return 0;
  const iso = `${match[1].slice(0, 4)}-${match[1].slice(4, 6)}-${match[1].slice(6, 8)}T${match[2].slice(0, 2)}:${match[2].slice(2, 4)}:${match[2].slice(4, 6)}`;
  const ms = new Date(iso).getTime();
  return Number.isFinite(ms) ? ms : 0;
};

export const getReolinkVideoSortMs = (video) => {
  const fromMeta = new Date(video?.recordedAt || video?.lastModified || 0).getTime();
  const fromName = getReolinkFileTimestampMs(video?.fileName || video?.s3Key);
  return Math.max(
    Number.isFinite(fromMeta) ? fromMeta : 0,
    fromName || 0,
  );
};

/** "Escambia 2" / "ESCAMBIA_2" / cameraId trailing digits → 2 */
export const getCameraNumberFromCamera = (camera) => {
  const blobs = [camera?.cameraName, camera?.cameraId, camera?.name]
    .filter(Boolean)
    .join(' ');
  const match = String(blobs).match(/(\d+)\s*$/);
  if (!match) return null;
  const n = Number(match[1]);
  return Number.isFinite(n) && n > 0 ? n : null;
};

export const formatCameraLabel = (cameraNumber, fallbackIndex = 0) =>
  `Camera ${cameraNumber || fallbackIndex + 1}`;

const displayBridgeName = (bridgeName) =>
  String(bridgeName || '')
    .replace(/\s+bridges?\s*$/i, '')
    .trim();

export const formatAlertsCameraFileLabel = (fileName, bridgeName) => {
  const n = getCameraNumberFromFileName(fileName);
  const camera = n ? formatCameraLabel(n) : '';
  const site = displayBridgeName(bridgeName);
  if (site && camera) return `${site} ${camera}`;
  if (camera) return camera;
  if (site) return site;
  if (!fileName) return 'Video';
  return String(fileName).replace(/\.[a-z0-9]+$/i, '') || 'Video';
};

const toPercent = (value) => {
  if (value == null || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.min(100, n));
};

export const normalizeReolinkCameraBattery = (camera, index = 0) => {
  const cameraNumber = getCameraNumberFromCamera(camera) || index + 1;
  const status = String(camera?.status || '').toLowerCase() || 'unknown';
  const source = String(camera?.source || '').toLowerCase() || 'unknown';
  return {
    cameraId: camera?.cameraId || camera?._id || `camera-${cameraNumber}`,
    cameraName: camera?.cameraName || camera?.name || formatCameraLabel(cameraNumber),
    cameraNumber,
    label: formatCameraLabel(cameraNumber),
    batteryPercent: toPercent(camera?.batteryPercent),
    isCharging: Boolean(camera?.isCharging),
    lowPower: Boolean(camera?.lowPower),
    status,
    source,
    updatedAt: camera?.updatedAt || null,
  };
};

export const normalizeReolinkCameraList = (cameras) =>
  asArray(cameras)
    .map((camera, index) => normalizeReolinkCameraBattery(camera, index))
    .sort((a, b) => a.cameraNumber - b.cameraNumber);

export const formatBatteryUpdatedAt = (updatedAt) => {
  if (!updatedAt) return '';
  const d = new Date(updatedAt);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};
